import { create } from 'zustand'
import type { PopupCampaignDisplayFrequency } from '@/services/popup-campaigns'

interface PopupCampaignModalState {
  isOpen: boolean
  openPopupModal: () => void
  closePopupModal: () => void
}

const DISMISS_KEY = 'dday-popup-dismissed'
const SESSION_KEY = 'dday-popup-session'

// Stores a future Unix ms timestamp — when the popup is next allowed to show.
// Cooldown is baked in at write time so the read check is just Date.now() > resumeAt.
function getResumeAt(): number | null {
  if (typeof window === 'undefined') return null
  const stored = localStorage.getItem(DISMISS_KEY)
  if (!stored) return null
  const parsed = parseInt(stored, 10)
  return isNaN(parsed) ? null : parsed
}

function setResumeAt(resumeAt: number): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(DISMISS_KEY, resumeAt.toString())
}

// setHours(24, 0, 0, 0) rolls over to 00:00:00.000 of the next calendar day in local time
function getMidnightTonight(): number {
  const d = new Date()
  d.setHours(24, 0, 0, 0)
  return d.getTime()
}

function hasShownThisSession(): boolean {
  if (typeof window === 'undefined') return false
  return sessionStorage.getItem(SESSION_KEY) === '1'
}

function markShownThisSession(): void {
  if (typeof window === 'undefined') return
  sessionStorage.setItem(SESSION_KEY, '1')
}

// frequency drives which storage layer is checked:
//   every_visit     → always show
//   once_per_session → sessionStorage (scoped to tab lifetime)
//   once_per_day    → localStorage resumeAt (persists across sessions)
export function shouldShowPopup(
  frequency: PopupCampaignDisplayFrequency
): boolean {
  if (frequency === 'every_visit') return true
  if (frequency === 'once_per_session') return !hasShownThisSession()
  const resumeAt = getResumeAt()
  return resumeAt === null || Date.now() > resumeAt
}

// Called the moment the popup opens — prevents re-trigger within the same session
export function recordPopupShown(): void {
  markShownThisSession()
}

// Light dismiss (Close button / overlay / Escape) — suppresses until midnight tonight only.
// The full N-day cooldown is only written when the user checks "Don't show again".
export function recordLightDismissal(): void {
  markShownThisSession()
  setResumeAt(getMidnightTonight())
}

// Explicit opt-out via "Don't show again for N days" checkbox
export function recordPopupDismissal(
  frequency: PopupCampaignDisplayFrequency,
  cooldownDays: number
): void {
  markShownThisSession()
  if (frequency !== 'every_visit') {
    setResumeAt(Date.now() + cooldownDays * 24 * 60 * 60 * 1000)
  }
}

export const usePopupCampaignModalStore = create<PopupCampaignModalState>(
  (set) => ({
    isOpen: false,
    openPopupModal: () => set({ isOpen: true }),
    closePopupModal: () => set({ isOpen: false }),
  })
)
